// ============================================================================
// utils_audioOnlySource.ts
// Sources « audio seul » du deck : un emplacement qui n'apporte que du son.
//
// Deux familles, choisies dans la fenêtre de réglage d'un emplacement :
//   • `audio-only:<entrée>` — une source audio précise (micro du poste, son d'un
//     smartphone, d'un partage d'écran, d'une playlist). L'entrée fait partie de
//     l'identifiant : elle est fixée au choix et ne se rechoisit pas.
//   • `audio-only:vide-<horodatage>` — « Vide (audio seul) » : une image vide dont
//     l'entrée audio se choisit ensuite, comme pour une caméra.
//
// Aucune n'apporte d'image : elles en GÉNÈRENT une (`audioVisual`), un écran noir
// (createBlackVideoStream, par défaut), un analyseur de spectre de leur son
// ou un oscilloscope (spectrumVideo.ts), et envoient le son de leur entrée `audio-<id>`.
//
// Elles n'existent que parce qu'un opérateur les a posées sur un emplacement :
// la liste est donc mémorisée par chaîne (clé de deckSettingsKeys, emportée par la
// sauvegarde serveur), faute de quoi l'élagage du deck — qui vide tout emplacement
// dont la source manque à `videoSources` — les ferait disparaître au rechargement.
// ============================================================================

import type { VideoSource, AudioOnlyVisual } from '../../contexts/MediaStreamContext';
import { deckSettingsKeys } from './deck_persistence';

export const AUDIO_ONLY_PREFIX = 'audio-only:';
const EMPTY_PREFIX             = `${AUDIO_ONLY_PREFIX}vide-`;
/** Valeur du <select> de la fenêtre de réglage pour « Vide (audio seul) » pas encore créé. */
export const NEW_EMPTY_AUDIO_ONLY_VALUE = `${AUDIO_ONLY_PREFIX}vide`;
export const EMPTY_AUDIO_ONLY_LABEL     = 'Vide (audio seul)';

/** Image d'une source audio seul — l'écran noir par défaut. */
export function audioOnlyVisual(s: VideoSource | null | undefined): AudioOnlyVisual {
  return s?.audioVisual === 'spectrum' || s?.audioVisual === 'oscilloscope' ? s.audioVisual : 'black';
}

export function isAudioOnlySource(s: VideoSource | null | undefined): boolean {
  return s?.type === 'audio';
}

/** « Vide (audio seul) » : l'entrée audio se choisit dans la fenêtre de réglage. */
export function isEmptyAudioOnlySource(s: VideoSource | null | undefined): boolean {
  return s?.type === 'audio' && s.id.startsWith(EMPTY_PREFIX);
}

/** Identifiant de la source audio seul attachée à cette entrée audio (deviceId étendu). */
export function audioOnlySourceId(audioDeviceId: string): string {
  return `${AUDIO_ONLY_PREFIX}${audioDeviceId}`;
}

export function makeAudioOnlySource(audioDeviceId: string, label: string): VideoSource {
  return { id: audioOnlySourceId(audioDeviceId), label, type: 'audio', deviceId: audioDeviceId };
}

export function makeEmptyAudioOnlySource(): VideoSource {
  return { id: `${EMPTY_PREFIX}${Date.now().toString(36)}`, label: EMPTY_AUDIO_ONLY_LABEL, type: 'audio' };
}

// ── Mémoire par chaîne ────────────────────────────────────────────────────────

export function loadAudioOnlySources(uuid: string | null): VideoSource[] {
  if (!uuid) return [];
  try {
    const raw = localStorage.getItem(deckSettingsKeys(uuid).audioOnlySources);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list)
      ? list.filter((s: any) => s && typeof s.id === 'string' && s.id.startsWith(AUDIO_ONLY_PREFIX))
            .map((s: any): VideoSource => ({
              id: s.id, label: String(s.label ?? ''), type: 'audio',
              ...(s.deviceId ? { deviceId: s.deviceId } : {}),
              ...(s.audioVisual === 'spectrum' || s.audioVisual === 'oscilloscope' ? { audioVisual: s.audioVisual as AudioOnlyVisual } : {}),
            }))
      : [];
  } catch { return []; }
}

export function saveAudioOnlySources(uuid: string | null, list: VideoSource[]): void {
  if (!uuid) return;
  const plain = list.map(s => ({
    id: s.id, label: s.label,
    ...(s.deviceId ? { deviceId: s.deviceId } : {}),
    ...(s.audioVisual && s.audioVisual !== 'black' ? { audioVisual: s.audioVisual } : {}),
  }));
  try { localStorage.setItem(deckSettingsKeys(uuid).audioOnlySources, JSON.stringify(plain)); } catch {}
}

// ── Image noire ───────────────────────────────────────────────────────────────

/**
 * Flux vidéo noir pour l'antenne d'une source audio seul. Le canvas est repeint
 * régulièrement : un canvas jamais redessiné ne livre pas d'image à captureStream,
 * et le compositeur de régie attendrait une première image qui ne vient pas.
 * La minuterie s'arrête d'elle-même quand la piste est arrêtée.
 */
export function createBlackVideoStream(width = 1280, height = 720): MediaStream {
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  const paint = () => { if (ctx) { ctx.fillStyle = '#000'; ctx.fillRect(0, 0, width, height); } };
  paint();
  const stream = canvas.captureStream(5);
  const track  = stream.getVideoTracks()[0];
  const timer  = setInterval(() => {
    if (!track || track.readyState === 'ended') { clearInterval(timer); return; }
    paint();
  }, 200);
  return stream;
}
